import { reportChanged } from "@/lib/api/changes"
import { notifySignedOut } from "@/lib/api/session"
import { env } from "@/lib/env"
import { ApiError, NetworkError } from "@/lib/api/errors"
import { reportServerTrouble } from "@/lib/api/outage"
import { clearToken, readToken } from "@/lib/api/token"

export type QueryParams = Record<
  string,
  string | number | boolean | Array<string | number> | null | undefined
>

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  body?: unknown
  params?: QueryParams
  signal?: AbortSignal
  anonymous?: boolean
}

export type UploadOptions = {
  method?: "POST" | "PUT"
  body: FormData
  onProgress?: (progress: number) => void
  signal?: AbortSignal
}

type Handler = () => void

let onUnauthenticated: Handler = () => {}
let onOrganisationAccessDenied: Handler = () => {}
let organisation: string | null = null

const organisationHeader = "x-organisation"

export function setUnauthenticatedHandler(handler: Handler): void {
  onUnauthenticated = handler
}

export function setOrganisationAccessDeniedHandler(handler: Handler): void {
  onOrganisationAccessDenied = handler
}

export function setOrganisation(slug: string | null): void {
  organisation = slug
}

export function currentOrganisation(): string | null {
  return organisation
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = new URL(
    path.startsWith("/") ? `${env.apiUrl}${path}` : `${env.apiUrl}/${path}`
  )

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === null || value === undefined || value === "") {
      continue
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        url.searchParams.append(key, String(entry))
      }
      continue
    }

    url.searchParams.set(key, String(value))
  }

  return url.toString()
}

export function authorisedHeaders(): Headers {
  const headers = new Headers({ accept: "application/json" })
  const token = readToken()

  if (token) {
    headers.set("authorization", `Bearer ${token}`)
  }

  if (organisation) {
    headers.set(organisationHeader, organisation)
  }

  return headers
}

function buildHeaders(options: RequestOptions): Headers {
  const headers = authorisedHeaders()

  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set("content-type", "application/json")
  }

  return headers
}

function buildBody(body: unknown): BodyInit | undefined {
  if (body === undefined) {
    return undefined
  }

  if (body instanceof FormData) {
    return body
  }

  return JSON.stringify(body)
}

async function readBody(response: Response): Promise<unknown> {
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return undefined
  }

  const contentType = response.headers.get("content-type") ?? ""

  if (!contentType.includes("json")) {
    return await response.text()
  }

  try {
    return await response.json()
  } catch {
    return undefined
  }
}

function readTextBody(contentType: string, text: string): unknown {
  if (!contentType.includes("json")) {
    return text
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    return undefined
  }
}

function signedOut(anonymous: boolean): void {
  clearToken()

  if (!anonymous) {
    notifySignedOut()
    onUnauthenticated()
  }
}

function throwResponseError(status: number, body: unknown): never {
  const error = ApiError.fromBody(status, body)

  if (error.isServerFault) {
    reportServerTrouble()
  }

  if (error.isOrganisationAccessDenied) {
    onOrganisationAccessDenied()
  }

  throw error
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  try {
    return await fetch(buildUrl(path, options.params), {
      method: options.method ?? "GET",
      headers: buildHeaders(options),
      body: buildBody(options.body),
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error
    }

    if (error instanceof ApiError) {
      throw error
    }

    reportServerTrouble()
    throw new NetworkError()
  }
}

async function request(
  path: string,
  options: RequestOptions
): Promise<Response> {
  const response = await send(path, options)

  if (response.status === 401) {
    signedOut(options.anonymous ?? false)
  }

  if ((options.method ?? "GET") !== "GET") {
    await reportChanged(response.headers.get("x-changed"))
  }

  if (!response.ok) {
    throwResponseError(response.status, await readBody(response))
  }

  return response
}

type UploadResponse = {
  body: unknown
  status: number
  changed: string | null
}

async function sendUpload(
  path: string,
  options: UploadOptions
): Promise<UploadResponse> {
  if (options.signal?.aborted) {
    throw new DOMException("The upload was cancelled.", "AbortError")
  }

  const method = options.method ?? "POST"

  const headers = buildHeaders({
    method,
    body: options.body,
    signal: options.signal,
  })

  return await new Promise<UploadResponse>((resolve, reject) => {
    const request = new XMLHttpRequest()
    let settled = false

    const finish = (complete: () => void) => {
      if (settled) {
        return
      }

      settled = true
      options.signal?.removeEventListener("abort", abort)
      complete()
    }

    const abort = () => request.abort()

    request.open(method, buildUrl(path))
    headers.forEach((value, name) => request.setRequestHeader(name, value))

    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        options.onProgress?.(
          Math.min(100, Math.round((event.loaded / event.total) * 100))
        )
      }
    })

    request.addEventListener("load", () => {
      finish(() => {
        resolve({
          body: readTextBody(
            request.getResponseHeader("content-type") ?? "",
            request.responseText
          ),
          status: request.status,
          changed: request.getResponseHeader("x-changed"),
        })
      })
    })

    request.addEventListener("error", () => {
      reportServerTrouble()
      finish(() => reject(new NetworkError()))
    })

    request.addEventListener("abort", () => {
      finish(() =>
        reject(new DOMException("The upload was cancelled.", "AbortError"))
      )
    })

    options.signal?.addEventListener("abort", abort, { once: true })
    request.send(options.body)
  })
}

export async function apiFetch<TResponse>(
  path: string,
  options: RequestOptions = {}
): Promise<TResponse> {
  const response = await request(path, options)

  return (await readBody(response)) as TResponse
}

export async function apiFetchBlob(
  path: string,
  options: RequestOptions = {}
): Promise<Blob> {
  const response = await request(path, options)

  return await response.blob()
}

export async function apiUpload<TResponse>(
  path: string,
  options: UploadOptions
): Promise<TResponse> {
  const response = await sendUpload(path, options)

  if (response.status === 401) {
    signedOut(false)
  }

  await reportChanged(response.changed)

  if (response.status < 200 || response.status >= 300) {
    throwResponseError(response.status, response.body)
  }

  return response.body as TResponse
}
